import { TextField } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';

/** Codes that call for a free-text companion — mirrors `SPECIFY_CODES` in gov-api. */
const SPECIFY_CODES = new Set(['other', 'unknown']);

export function needsSpecify(code: string | null | undefined): boolean {
  return code != null && SPECIFY_CODES.has(code);
}

interface SpecifyFieldProps {
  /** The coded value the companion hangs off. */
  code: string | null | undefined;
  value: string;
  onChange: (value: string) => void;
  /** Overrides the default label; use where "other" needs naming, not describing. */
  labelKey?: string;
  /** Follows the coded field it hangs off when that field is frozen. */
  disabled?: boolean;
}

/**
 * The free-text companion to a dropdown whose value set ends in `other` or
 * `unknown`. Renders nothing until the code calls for it, so a form can mount
 * one per dropdown without a conditional at every call site.
 *
 * Two labels, because the two codes ask different questions: `other` asks what
 * the value was, `unknown` asks why it could not be determined. Answering the
 * first when the form meant the second is how a field fills up with "n/a".
 *
 * Deliberately not `required`: the API accepts a bare `other`, and a hard
 * requirement here would block saving a partially worked intake. The officer is
 * prompted, not stopped.
 */
export function SpecifyField({ code, value, onChange, labelKey, disabled }: SpecifyFieldProps) {
  const { t } = useTranslation();
  if (!needsSpecify(code)) return null;

  const key = labelKey ?? (code === 'unknown' ? 'gov.specify.unknown' : 'gov.specify.other');
  return (
    <TextField
      fullWidth
      size="small"
      label={t(key)}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{ htmlInput: { maxLength: 200 } }}
    />
  );
}
